/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {z} from 'zod'
import {useTranslation} from 'react-i18next'
import {useMemo} from 'react'

export const useLeadBasicSchema = (isOpportunity: boolean = false) => {
    const { t } = useTranslation()

    return useMemo(() => {
        const optionalString = z.string().trim().nullable().optional()
        const optionalEmail = z.string().email(t('Invalid email format')).nullable().or(z.literal('')).optional()
        const optionalUrl = z.string().url(t('Invalid URL format')).nullable().or(z.literal('')).optional()

        return z
            .object({
                name: z
                    .string()
                    .trim()
                    .min(
                        1,
                        t(
                            isOpportunity
                                ? 'Opportunity name is required'
                                : 'Lead name is required'
                        )
                    )
                    .max(120, t('Name must be at most 120 characters')),
                company_name: z
                    .string()
                    .trim()
                    .max(120, t('Company name must be at most 120 characters'))
                    .nullable()
                    .optional(),
                full_name: z
                    .string()
                    .trim()
                    .max(120, t('Full name must be at most 120 characters'))
                    .nullable()
                    .optional(),
                first_name: z
                    .string()
                    .trim()
                    .max(120, t('First name must be at most 120 characters'))
                    .nullable()
                    .optional(),
                title: z
                    .string()
                    .trim()
                    .max(120, t('Title must be at most 120 characters'))
                    .nullable()
                    .optional(),
                custom_title: z
                    .string()
                    .trim()
                    .max(120, t('Custom title must be at most 120 characters'))
                    .nullable()
                    .optional(),
                position: z
                    .string()
                    .trim()
                    .max(120, t('Position must be at most 120 characters'))
                    .nullable()
                    .optional(),
                anlasstyp: z
                    .string()
                    .trim()
                    .max(120, t('Anlasstyp must be at most 120 characters'))
                    .nullable()
                    .optional(),
                contact_origin: optionalString,
                company_origin: optionalString,
                contact_note: z.string().max(1000, t('Note must be at most 1000 characters')).nullable().optional(),
                last_name: z
                    .string()
                    .trim()
                    .max(120, t('Last name must be at most 120 characters'))
                    .nullable()
                    .optional(),
                gender: z.enum(['male', 'female', 'other']).nullable().optional(),
                company_type: optionalString,
                business_category: optionalString,
                email: optionalEmail,
                phone: z
                    .string()
                    // .regex(/^(?=.*\d)[\d \-:/\\]+$/u, t('Phone must contain valid characters'))
                    .max(30, t('Phone must be at most 30 characters'))
                    .nullable()
                    .optional(),
                contact_email: optionalEmail,
                contact_phone: z
                    .string()
					// .regex(/^(?=.*\d)[\d \-:/\\]+$/u, t('Phone must contain valid characters'))
                    .max(30, t('Phone must be at most 30 characters'))
                    .nullable()
                    .optional(),
                website: optionalUrl,
                source: optionalString,
                referral_id: optionalString,
                category: optionalString,
                requested_date: z.date().nullable().optional(),
                created_date: z.date().nullable().optional(),
                closed_at: z.date().nullable().optional(),
                persons: z.number().min(0, t('Min 0')).nullable().optional(),
                seller_id: z.number().nullable().optional(),
                tags: z.array(z.string()).nullable().optional(),
                notes: z.string().max(1000, t('Notes must be at most 1000 characters')).nullable().optional(),
                street_address: optionalString,
                city: optionalString,
                postal_code: optionalString,
                state: optionalString,
                country: optionalString,
                is_new_contact: z.boolean().optional(),
                contact_id: z.number().nullable().optional(),
                company_id: z.number().nullable().optional(),
            })
            .superRefine((data, ctx) => {
                if (data.is_new_contact) {
                    const hasEmail = data.contact_email && data.contact_email.trim() !== ''
                    const hasPhone = data.contact_phone && data.contact_phone.trim() !== ''

                    if (!hasEmail && !hasPhone) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: t('Email or Phone is required for new contact'),
                            path: ['contact_email'],
                        })
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: t('Email or Phone is required for new contact'),
                            path: ['contact_phone'],
                        })
                    }
                }

                const rawCountry = (data.country || '').trim()
                const normalizedCountry = rawCountry
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .toLowerCase()

                const isSwissCountry = ['switzerland', 'schweiz', 'suisse', 'svajciarsko'].includes(
                    normalizedCountry
                )

                const hasAddressField =
                    (data.street_address && data.street_address.trim() !== '') ||
                    (data.postal_code && data.postal_code.trim() !== '') ||
                    (data.city && data.city.trim() !== '')

                if (isSwissCountry && hasAddressField && !(data.state && data.state.trim() !== '')) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: t('Kanton is required for Switzerland addresses'),
                        path: ['state'],
                    })
                }
            })
    }, [t, isOpportunity])
}

export type LeadBasicInputs = z.infer<ReturnType<typeof useLeadBasicSchema>>
